import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  User,
  CreditCard,
  Calendar,
  Check,
  AlertTriangle,
  Loader2,
  LogOut,
  Crown,
  Sparkles,
  Coins,
  Music,
  Share2,
  Copy,
  Gift,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import CompleteProfileModal from "../components/CompleteProfileModal";

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [referral, setReferral] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [downloads, setDownloads] = useState([]);
  const [confirmingPack, setConfirmingPack] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  // After Stripe Checkout success for a pack purchase, the user lands here with ?pack_session=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("pack_session");
    if (!sessionId || !user) return;
    setConfirmingPack(true);
    axios
      .get(`${API}/packs/checkout-status/${sessionId}`)
      .then((resp) => {
        if (resp.data.status === "paid") {
          toast.success("Payment confirmed! Your downloads are ready below.");
        } else {
          toast.info(`Payment status: ${resp.data.payment_status || resp.data.status}. We'll update as soon as it confirms.`);
        }
      })
      .catch((e) => {
        console.error("Pack confirm failed", e);
        toast.error("Could not verify pack purchase. Please refresh in a moment.");
      })
      .finally(() => {
        setConfirmingPack(false);
        // Clean the URL so refreshing doesn't re-trigger
        window.history.replaceState({}, "", "/account");
        fetchDownloads();
      });
  }, [user]);

  const fetchDownloads = async () => {
    try {
      const resp = await axios.get(`${API}/me/downloads`);
      setDownloads(resp.data.songs || []);
    } catch (e) {
      console.error("Failed to fetch downloads", e);
    }
  };

  const fetchData = async () => {
    try {
      const [subRes, creditsRes, refRes, profileRes, dlRes] = await Promise.all([
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/credits`),
        axios.get(`${API}/referral`).catch(() => ({ data: null })),
        axios.get(`${API}/profile`).catch(() => ({ data: null })),
        axios.get(`${API}/me/downloads`).catch(() => ({ data: { songs: [] } })),
      ]);
      setSubscription(subRes.data);
      setCredits(creditsRes.data);
      setReferral(refRes.data);
      setProfile(profileRes.data);
      setDownloads(dlRes.data.songs || []);

      // Show profile modal if incomplete and user has subscription
      if (profileRes.data && !profileRes.data.profile_complete && subRes.data?.has_subscription) {
        const dismissed = sessionStorage.getItem("profile_prompt_dismissed");
        if (!dismissed) setShowProfileModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (purchaseId, contentId, displayName) => {
    try {
      const resp = await axios.get(`${API}/downloads/file/${purchaseId}/${contentId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "audio/mpeg" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${displayName || "barsongz"}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("Download failed. Please try again.");
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription", error);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel? You'll keep access until the end of your billing period.")) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {});
      toast.success("Subscription will be canceled at the end of your billing period");
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/subscription/reactivate`, {});
      toast.success("Subscription reactivated!");
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: "Active", className: "bg-green-500/20 text-green-500 border-green-500/50" },
      trialing: { label: "Trial", className: "bg-gold/20 text-gold border-gold/50" },
      canceled: { label: "Canceled", className: "bg-destructive/20 text-destructive border-destructive/50" },
      past_due: { label: "Past Due", className: "bg-orange-500/20 text-orange-500 border-orange-500/50" },
      incomplete: { label: "Incomplete", className: "bg-muted text-muted-foreground border-muted" },
    };
    const config = statusConfig[status] || statusConfig.incomplete;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const sub = subscription?.subscription;
  const hasSubscription = subscription?.has_subscription;

  return (
    <div className="min-h-screen py-8" data-testid="account-page">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="font-serif text-3xl font-bold mb-8">My Account</h1>

        <div className="space-y-6">
          {/* Profile Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center">
                    <User className="h-8 w-8 text-gold" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="font-serif">{user.name || "User"}</CardTitle>
                    {user.is_founding_member && (
                      <Badge 
                        className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black border-0 text-xs"
                        data-testid="founding-member-badge"
                      >
                        <Sparkles className="h-3 w-3 mr-1" />
                        Founding Member
                      </Badge>
                    )}
                  </div>
                  <CardDescription>{user.email}</CardDescription>
                  {user.founding_member_since && (
                    <p className="text-xs text-amber-500 mt-1">
                      Founding member since {new Date(user.founding_member_since).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Subscription Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-serif flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscription
                </CardTitle>
                {hasSubscription && getStatusBadge(sub?.status)}
              </div>
            </CardHeader>
            <CardContent>
              {hasSubscription ? (
                <div className="space-y-4">
                  {/* Plan Info */}
                  <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                    user.is_founding_member 
                      ? 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/30' 
                      : 'bg-gold/10 border-gold/20'
                  }`}>
                    {user.is_founding_member ? (
                      <Sparkles className="h-8 w-8 text-amber-500" />
                    ) : (
                      <Crown className="h-8 w-8 text-gold" />
                    )}
                    <div>
                      <p className="font-semibold">
                        {user.is_founding_member ? 'Founding Member Plan' : 'Full Access Plan'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {user.is_founding_member
                          ? '$5.99/month (founding rate) • Unlimited streaming & downloads'
                          : '$9.99/month • Unlimited streaming & downloads'}
                      </p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {sub?.trial_end && sub?.status === "trialing" && (
                      <div>
                        <p className="text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Trial Ends
                        </p>
                        <p className="font-medium">{formatDate(sub.trial_end)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {sub?.cancel_at_period_end ? "Access Until" : "Next Billing"}
                      </p>
                      <p className="font-medium">{formatDate(sub?.current_period_end)}</p>
                    </div>
                  </div>

                  {/* Cancellation Notice */}
                  {sub?.cancel_at_period_end && (
                    <div className="flex items-start gap-3 p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                      <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-500">Subscription Ending</p>
                        <p className="text-sm text-muted-foreground">
                          Your subscription will end on {formatDate(sub?.current_period_end)}. 
                          You'll still have access until then.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    {sub?.cancel_at_period_end ? (
                      <Button
                        onClick={handleReactivate}
                        disabled={actionLoading}
                        className="bg-gold text-primary-foreground hover:bg-gold/90"
                        data-testid="reactivate-btn"
                      >
                        {actionLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        Reactivate Subscription
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={handleCancelSubscription}
                        disabled={actionLoading}
                        data-testid="cancel-btn"
                      >
                        {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">You don't have an active subscription.</p>
                  <Button 
                    onClick={() => navigate("/subscribe")}
                    className="bg-gold text-primary-foreground hover:bg-gold/90"
                    data-testid="subscribe-cta"
                  >
                    Subscribe Now
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Downloads */}
          <Card data-testid="my-downloads-card">
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="font-serif flex items-center gap-2">
                  <Download className="h-5 w-5 text-gold" />
                  My Downloads
                  {downloads.length > 0 && (
                    <Badge variant="outline" className="ml-2 border-gold text-gold">
                      {downloads.length}
                    </Badge>
                  )}
                </CardTitle>
                {confirmingPack && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Verifying purchase…
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {downloads.length === 0 ? (
                <div className="text-center py-6">
                  <Download className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-4">
                    You don't own any downloads yet. Grab a Course Pack or pick 4 songs to keep forever.
                  </p>
                  <Button
                    onClick={() => navigate("/packs")}
                    className="bg-gold text-primary-foreground hover:bg-gold/90"
                    data-testid="browse-packs-btn"
                  >
                    Browse Packs
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {downloads.map((s) => (
                    <div
                      key={s.content_id}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                      data-testid={`download-row-${s.content_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {s.song_title || s.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {s.course_name || "Bar Songz"}{s.topic ? ` · ${s.topic}` : ""}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleDownload(s.purchase_id, s.content_id, s.song_title || s.title)
                        }
                        data-testid={`download-btn-${s.content_id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground pt-2">
                    Each MP3 includes your name as the licensee in the file metadata. For personal study use only.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Credits Card - hidden for users with zero credits (retired in favor of packs) */}
          {credits && credits.audio_credits > 0 && (
            <Card data-testid="credits-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif flex items-center gap-2">
                    <Coins className="h-5 w-5 text-gold" />
                    Song Credits
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Current Balance */}
                  <div className="flex items-center justify-between p-4 bg-gold/10 rounded-lg border border-gold/20">
                    <div className="flex items-center gap-3">
                      <Music className="h-5 w-5 text-gold" />
                      <span className="text-muted-foreground">Available Credits</span>
                    </div>
                    <span className="font-bold text-2xl text-gold" data-testid="credits-balance">
                      {credits?.audio_credits || 0}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground">
                    {hasSubscription 
                      ? "You have unlimited streaming with your subscription, but your credits will be saved for later."
                      : "Use credits to stream individual songs. Each stream uses 1 credit."}
                  </p>

                  {/* Buy More Button — retired in favor of packs */}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Referral Card */}
          {referral && (
            <Card data-testid="referral-card">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Refer a Friend
                </CardTitle>
                <CardDescription>
                  Share your link — you both get 10 bonus credits + a free month when they subscribe.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={`${window.location.origin}/subscribe?ref=${referral.referral_code}`}
                      className="text-sm"
                      data-testid="referral-link-input"
                    />
                    <Button
                      variant="outline"
                      className="shrink-0 border-gold/50 text-gold hover:bg-gold hover:text-primary-foreground"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/subscribe?ref=${referral.referral_code}`);
                        toast.success("Referral link copied!");
                      }}
                      data-testid="copy-referral-btn"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-card rounded-lg border border-border/40">
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-gold" />
                      <span className="text-sm">Referrals used</span>
                    </div>
                    <span className="font-semibold" data-testid="referral-count">
                      {referral.referral_count} / {referral.max_referrals}
                    </span>
                  </div>

                  {referral.referral_count >= referral.max_referrals && (
                    <p className="text-xs text-muted-foreground text-center">
                      You've reached the maximum number of referrals. Thanks for spreading the word!
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Logout */}
          <Card>
            <CardContent className="py-4">
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="w-full"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {showProfileModal && (
        <CompleteProfileModal
          onClose={() => {
            setShowProfileModal(false);
            sessionStorage.setItem("profile_prompt_dismissed", "true");
          }}
          onComplete={() => fetchData()}
        />
      )}
    </div>
  );
}
